import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AddressManager = ({ onSelect, selectedId }) => {
  const [addresses, setAddresses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
  const [pincodeLookup, setPincodeLookup] = useState({ loading: false, error: '', autoFilled: false });

  const lookupPincode = async (pc) => {
    if (pc.length !== 6) { setPincodeLookup({ loading: false, error: '', autoFilled: false }); return; }
    setPincodeLookup({ loading: true, error: '', autoFilled: false });
    try {
      const { data } = await axios.get(`${API}/api/shipping/postcode-details/${pc}`);
      if (data && (data.city || data.state)) {
        setForm((f) => ({ ...f, city: data.city || f.city, state: data.state || f.state }));
        setPincodeLookup({ loading: false, error: '', autoFilled: true });
      } else {
        setPincodeLookup({ loading: false, error: 'No city/state for this pincode', autoFilled: false });
      }
    } catch (err) {
      setPincodeLookup({ loading: false, error: err.response?.data?.detail || 'Pincode lookup failed', autoFilled: false });
    }
  };

  const onPincodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setForm({ ...form, pincode: v });
    if (v.length === 6) lookupPincode(v);
    else setPincodeLookup({ loading: false, error: '', autoFilled: false });
  };

  useEffect(() => { fetchAddresses(); }, []);

  const fetchAddresses = async () => {
    try {
      const { data } = await axios.get(`${API}/api/addresses`, { withCredentials: true });
      setAddresses(data);
      if (data.length > 0 && !selectedId) {
        const def = data.find(a => a.is_default) || data[0];
        onSelect?.(def.address_id);
      }
    } catch {}
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    if (!form.pincode || form.pincode.length !== 6 || !/^\d{6}$/.test(form.pincode)) {
      toast.error('Pincode must be exactly 6 digits');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/api/addresses/${editing.address_id}`, form, { withCredentials: true });
        toast.success('Address updated');
      } else {
        await axios.post(`${API}/api/addresses`, form, { withCredentials: true });
        toast.success('Address added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ first_name: '', last_name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
      fetchAddresses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/api/addresses/${id}`, { withCredentials: true });
      toast.success('Address deleted');
      fetchAddresses();
    } catch { toast.error('Failed to delete'); }
  };

  const setDefault = async (id) => {
    try {
      await axios.put(`${API}/api/addresses/${id}/default`, {}, { withCredentials: true });
      fetchAddresses();
    } catch {}
  };

  const openEdit = (addr) => {
    setEditing(addr);
    setForm({ first_name: addr.first_name || '', last_name: addr.last_name || '', phone: addr.phone, line1: addr.line1, line2: addr.line2 || '', city: addr.city, state: addr.state, pincode: addr.pincode });
    setDialogOpen(true);
  };

  return (
    <div data-testid="address-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-sm">Delivery Address</h3>
        <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => { setEditing(null); setForm({ first_name: '', last_name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' }); setDialogOpen(true); }} data-testid="add-address-button">
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>

      {addresses.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-address-message">No addresses yet. Add one to proceed.</p>}

      <div className="space-y-2">
        {addresses.map((addr) => (
          <div
            key={addr.address_id}
            onClick={() => onSelect?.(addr.address_id)}
            className={`p-3 rounded-lg border text-sm cursor-pointer transition-colors ${selectedId === addr.address_id ? 'border-primary bg-primary/5' : 'border-[#E0E0E0] hover:border-primary/50'}`}
            data-testid={`address-${addr.address_id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{[addr.first_name, addr.last_name].filter(Boolean).join(' ') || addr.name} <span className="text-muted-foreground font-normal">| {addr.phone}</span></p>
                <p className="text-muted-foreground text-xs">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}</p>
                <p className="text-muted-foreground text-xs">{addr.city}, {addr.state} — {addr.pincode}</p>
                {addr.is_default && <span className="text-[10px] text-primary font-semibold uppercase">Default</span>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {!addr.is_default && (
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); setDefault(addr.address_id); }} data-testid={`set-default-${addr.address_id}`}>
                    <Check className="h-3 w-3" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); openEdit(addr); }} data-testid={`edit-address-${addr.address_id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handleDelete(addr.address_id); }} data-testid={`delete-address-${addr.address_id}`}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Address' : 'Add Address'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3" data-testid="address-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required data-testid="address-first-name-input" /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required data-testid="address-last-name-input" /></div>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required data-testid="address-phone-input" /></div>
            <div><Label>Address Line 1</Label><Input value={form.line1} onChange={(e) => setForm({...form, line1: e.target.value})} required data-testid="address-line1-input" /></div>
            <div><Label>Address Line 2</Label><Input value={form.line2} onChange={(e) => setForm({...form, line2: e.target.value})} data-testid="address-line2-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={onPincodeChange} maxLength={6} required data-testid="address-pincode-input" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} required data-testid="address-city-input" placeholder={pincodeLookup.loading ? 'Looking up…' : ''} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} required data-testid="address-state-input" placeholder={pincodeLookup.loading ? 'Looking up…' : ''} /></div>
            </div>
            {pincodeLookup.autoFilled && <p className="text-xs text-primary -mt-1" data-testid="address-pincode-autofilled">City &amp; state auto-filled from pincode.</p>}
            {pincodeLookup.error && <p className="text-xs text-destructive -mt-1" data-testid="address-pincode-error">{pincodeLookup.error}. Please enter manually.</p>}
            <Button type="submit" className="w-full" data-testid="save-address-button">{editing ? 'Update' : 'Save'} Address</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddressManager;
